import { useCallback, useEffect, useState } from 'react';
import { useSystem } from '@ohif/core';
import {
  ReportAssessmentService,
  TrackedAssessment,
  hasSubmittableRows,
  isNoFindings,
} from '../services/ReportAssessmentService';

export function useReportAssessment(
  studyInstanceUid: string,
  onClose: () => void
): {
  rows: readonly TrackedAssessment[];
  isNoFindings: boolean;
  isSubmitDisabled: boolean;
  setClassification: (index: number, value?: boolean) => void;
  setDetection: (index: number, value?: boolean) => void;
  submit: () => Promise<void>;
} {
  const { servicesManager } = useSystem();
  const { reportAssessmentService, diagnosisService } = servicesManager.services;

  const [rows, setRows] = useState<readonly TrackedAssessment[]>(
    () => reportAssessmentService.getStudy(studyInstanceUid).assessments
  );

  useEffect(() => {
    const read = ({ studyInstanceUid: changed }: { studyInstanceUid: string }) => {
      if (changed === studyInstanceUid) {
        setRows(reportAssessmentService.getStudy(studyInstanceUid).assessments);
      }
    };

    const subscriptions = [
      reportAssessmentService.subscribe(ReportAssessmentService.EVENTS.ASSESSMENT_UPDATED, read),
      reportAssessmentService.subscribe(ReportAssessmentService.EVENTS.ASSESSMENT_SUBMITTED, read),
    ];

    setRows(reportAssessmentService.getStudy(studyInstanceUid).assessments);

    return () => subscriptions.forEach(subscription => subscription.unsubscribe());
  }, [reportAssessmentService, studyInstanceUid]);

  const setClassification = useCallback(
    (index: number, value?: boolean) =>
      reportAssessmentService.setVerdict(
        studyInstanceUid,
        index,
        'isClassificationCorrect',
        value
      ),
    [reportAssessmentService, studyInstanceUid]
  );

  const setDetection = useCallback(
    (index: number, value?: boolean) =>
      reportAssessmentService.setVerdict(studyInstanceUid, index, 'isDetectionCorrect', value),
    [reportAssessmentService, studyInstanceUid]
  );

  return {
    rows,

    isNoFindings: isNoFindings(rows),
    isSubmitDisabled: !hasSubmittableRows(rows),

    setClassification,
    setDetection,

    submit: useCallback(async () => {
      await diagnosisService.submitAssessment(studyInstanceUid);
      onClose();
    }, [diagnosisService, studyInstanceUid, onClose]),
  };
}
